import { PAGE_ROUTE_REGISTRY } from './pageRegistry';

export interface ResolvedStatCard {
  id: string;
  label: string;
  accent?: boolean;
  visible: boolean;
}

export interface ResolvedColumn {
  id: string;
  label: string;
  align?: string;
  visible: boolean;
}

export interface ResolvedTable {
  id: string;
  title: string;
  columns: ResolvedColumn[];
}

export interface ResolvedField {
  id: string;
  label: string;
  visible: boolean;
  isDate: boolean;
}

export interface ResolvedSection {
  id: string;
  title: string;
  enabled: boolean;
  fields: ResolvedField[];
}

export interface ResolvedPage {
  id: string;
  navLabel: string;
  path: string;
  iconName?: string;
  enabled: boolean;
  showInNav: boolean;
  statCards: ResolvedStatCard[];
  tables: ResolvedTable[];
  sections: ResolvedSection[];
}

export interface ResolvedPortalConfig {
  maxDateRangeDays: number;
  pages: ResolvedPage[];
  currencies: string[];
  timezones: string[];
  infoPageContent: string | null;
}

// apiConfig is the raw shape from GET /me/portal-config:
// { maxDateRangeDays, pages (operator overrides), registry (full page defs from backend) }
export function resolvePortalConfig(apiConfig: any): ResolvedPortalConfig {
  const registry: any[] = apiConfig?.registry ?? [];

  // Before the API responds (registry is empty), build a minimal config from the
  // local routing registry so nav items still render during the loading state.
  if (!registry.length) {
    return {
      maxDateRangeDays: apiConfig?.maxDateRangeDays ?? 14,
      currencies: apiConfig?.currencies ?? [],
      timezones: apiConfig?.timezones ?? ['UTC'],
      infoPageContent: apiConfig?.infoPageContent ?? null,
      pages: PAGE_ROUTE_REGISTRY.map((r) => ({
        id: r.id,
        navLabel: r.id,
        path: r.path,
        iconName: r.iconName,
        enabled: true,
        showInNav: true,
        statCards: [],
        tables: [],
        sections: [],
      })),
    };
  }

  const pages = registry.map((def: any) => {
    const route = PAGE_ROUTE_REGISTRY.find((r) => r.id === def.id);
    const pageOverride = apiConfig?.pages?.[def.id] ?? {};

    return {
      id: def.id,
      navLabel: pageOverride.navLabel || def.defaultNavLabel,
      path: route?.path ?? `/${def.id}`,
      iconName: def.iconName ?? route?.iconName,
      enabled: pageOverride.enabled ?? true,
      showInNav: def.showInNav ?? true,
      statCards: (def.statCards ?? []).map((sc: any) => ({
        id: sc.id,
        label: pageOverride.statCards?.[sc.id]?.label || sc.defaultLabel,
        accent: sc.accent,
        visible: pageOverride.statCards?.[sc.id]?.visible ?? true,
      })),
      tables: (def.tables ?? []).map((tbl: any) => ({
        id: tbl.id,
        title: pageOverride.tables?.[tbl.id]?.title || tbl.defaultTitle,
        columns: (tbl.columns ?? []).map((col: any) => ({
          id: col.id,
          label: pageOverride.tables?.[tbl.id]?.columns?.[col.id]?.label || col.defaultLabel,
          align: col.align,
          visible: pageOverride.tables?.[tbl.id]?.columns?.[col.id]?.visible ?? true,
        })),
      })),
      sections: (def.sections ?? []).map((sec: any) => ({
        id: sec.id,
        title: pageOverride.sections?.[sec.id]?.title || sec.defaultTitle,
        enabled: pageOverride.sections?.[sec.id]?.enabled ?? true,
        fields: (sec.fields ?? []).map((field: any) => ({
          id: field.id,
          label: pageOverride.sections?.[sec.id]?.fields?.[field.id]?.label || field.defaultLabel,
          visible: pageOverride.sections?.[sec.id]?.fields?.[field.id]?.visible ?? true,
          isDate: field.isDate ?? false,
        })),
      })),
    };
  });

  return {
    maxDateRangeDays: apiConfig?.maxDateRangeDays ?? 14,
    currencies: apiConfig?.currencies ?? [],
    timezones: apiConfig?.timezones ?? ['UTC'],
    infoPageContent: apiConfig?.infoPageContent ?? null,
    pages,
  };
}
